import React, { useState } from 'react';
import { MapPin, Search, X } from 'lucide-react';

// Sample CIL data - in production this would come from an API
const sampleCILs = [
  {
    id: 1,
    name: "Bay Area ILC",
    address: "3075 Adeline Street, Berkeley, CA 94703",
    phone: "(510) 841-4776",
    email: "info@bailc.org",
    website: "www.bailc.org",
    coordinates: { lat: 37.7749, lng: -122.4194 }
  },
  {
    id: 2,
    name: "Boston Center for Independent Living",
    address: "60 Temple Place, Boston, MA 02111",
    phone: "(617) 338-6665",
    email: "info@bostoncil.org",
    website: "www.bostoncil.org",
    coordinates: { lat: 42.3601, lng: -71.0589 }
  },
  // Add more sample data as needed
];

const InteractiveMap = () => {
  const [selectedState, setSelectedState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCIL, setSelectedCIL] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Filter CILs based on search query
  const filteredCILs = sampleCILs.filter(cil => 
    cil.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cil.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStateClick = (state) => {
    setSelectedState(state);
    // In production, this would fetch CILs for the selected state
  };

  const handleCILClick = (cil) => {
    setSelectedCIL(cil);
    setShowModal(true);
  };

  return (
    <div className="w-full">
      {/* Search and Filter Section */}
      <div className="mb-6">
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name or location..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          <select
            className="border rounded-lg px-4 py-2"
            value={selectedState}
            onChange={(e) => handleStateClick(e.target.value)}
          >
            <option value="">Select State</option>
            <option value="CA">California</option>
            <option value="NY">New York</option>
            <option value="TX">Texas</option>
            {/* Add more states */}
          </select>
        </div>
      </div>

      {/* Map and Results Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Interactive Map */}
        <div className="bg-gray-100 rounded-lg p-4 h-96">
          <div className="relative w-full h-full">
            {/* Map Placeholder - Replace with actual map implementation */}
            <div className="absolute inset-0 bg-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">Interactive Map</p>
            </div>
            
            {/* Map Markers */}
            {filteredCILs.map(cil => (
              <button
                key={cil.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 hover:z-10"
                style={{
                  left: `${(cil.coordinates.lng + 180) * (100 / 360)}%`,
                  top: `${(90 - cil.coordinates.lat) * (100 / 180)}%`
                }}
                onClick={() => handleCILClick(cil)}
              >
                <MapPin className="text-blue-600 hover:text-blue-800" />
              </button>
            ))}
          </div>
        </div>

        {/* Results List */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-lg font-semibold mb-4">
            Centers for Independent Living
            {selectedState && ` in ${selectedState}`}
          </h3>
          
          <div className="space-y-4">
            {filteredCILs.map(cil => (
              <button
                key={cil.id}
                className="w-full text-left p-4 rounded-lg border hover:border-blue-500 hover:shadow-md transition-all"
                onClick={() => handleCILClick(cil)}
              >
                <h4 className="font-semibold text-blue-600">{cil.name}</h4>
                <p className="text-gray-600 text-sm mt-1">{cil.address}</p>
                <p className="text-gray-500 text-sm mt-1">{cil.phone}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CIL Details Modal */}
      {showModal && selectedCIL && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold">{selectedCIL.name}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-3">
              <p className="flex items-start gap-2">
                <MapPin size={20} className="text-blue-600 flex-shrink-0 mt-1" />
                <span>{selectedCIL.address}</span>
              </p>
              <p><strong>Phone:</strong> {selectedCIL.phone}</p>
              <p><strong>Email:</strong> {selectedCIL.email}</p>
              <p><strong>Website:</strong> {selectedCIL.website}</p>
              
              <div className="flex gap-2 mt-6">
                <button
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  onClick={() => window.open(selectedCIL.website, '_blank')}
                >
                  Visit Website
                </button>
                <button
                  className="flex-1 border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50"
                  onClick={() => {/* Add to favorites/save logic */}}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveMap;